import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { getXPLeaderboard } from '../actions/xpActions';
import { getPrograms } from '../actions/programActions';
import { Trophy, Zap, BookOpen, Award, Medal, Crown, ChevronDown, MapPin, Flame, Users, TrendingUp, Hash } from 'lucide-react';

const METRIC_CONFIG = {
  xp: { label: 'XP', icon: Zap, color: '#f59e0b' },
  badges: { label: 'Badges', icon: Award, color: '#8b5cf6' },
  lessons: { label: 'Lessons', icon: BookOpen, color: '#10b981' },
  courses: { label: 'Missions', icon: Flame, color: '#ef4444' },
};

// ── Profile Hover Card ──────────────────────────────────────────────────────
class ProfileHoverCard extends Component {
  state = { visible: false, timeout: null };

  show = () => {
    clearTimeout(this.state.timeout);
    this.setState({ visible: true });
  };

  hide = () => {
    const timeout = setTimeout(() => this.setState({ visible: false }), 200);
    this.setState({ timeout });
  };

  componentWillUnmount() {
    clearTimeout(this.state.timeout);
  }

  render() {
    const { user, children, position } = this.props;
    const { visible } = this.state;
    const profileUrl = user?.profilePicture?.url;
    const initials = ((user?.name?.[0] || '') + (user?.lastName?.[0] || '')).toUpperCase();
    const fullName = `${user?.name || ''} ${user?.lastName || ''}`.trim();

    const posClass = position === 'below'
      ? 'top-full left-0 mt-2'
      : 'bottom-full left-0 mb-2';

    return (
      <div
        className="relative"
        onMouseEnter={this.show}
        onMouseLeave={this.hide}
      >
        {children}
        {visible && user && (
          <div
            className={`absolute z-50 ${posClass} w-64 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-fade-in`}
            onMouseEnter={this.show}
            onMouseLeave={this.hide}
          >
            <div className="h-16 bg-gradient-to-r from-[#054581] via-[#1086a4] to-[#1db5d4]" />
            <div className="flex justify-center -mt-8">
              {profileUrl ? (
                <img
                  src={profileUrl}
                  alt=""
                  className="w-16 h-16 rounded-full border-4 border-white object-cover shadow-md"
                />
              ) : (
                <div className="w-16 h-16 rounded-full border-4 border-white bg-gradient-to-br from-[#1086a4] to-[#054581] flex items-center justify-center text-white text-lg font-bold shadow-md">
                  {initials}
                </div>
              )}
            </div>
            <div className="px-4 pb-4 pt-2 text-center">
              <p className="font-semibold text-gray-800 text-sm">{fullName}</p>
              {user.country && (
                <div className="flex items-center justify-center gap-1 mt-1 text-xs text-gray-400">
                  <MapPin size={12} />
                  <span>{user.country}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}

// ── Top 3 Podium Card ───────────────────────────────────────────────────────
const PodiumCard = ({ entry, rank, isCurrentUser, activeTab }) => {
  const profileUrl = entry.user?.profilePicture?.url;
  const initials = ((entry.user?.name?.[0] || '') + (entry.user?.lastName?.[0] || '')).toUpperCase();

  const rankConfig = {
    1: {
      size: 'w-24 h-24',
      ring: 'ring-4 ring-yellow-400',
      badge: 'bg-gradient-to-br from-yellow-400 to-amber-500',
      icon: Crown,
      glow: 'shadow-[0_0_25px_rgba(245,158,11,0.35)]',
      order: 'order-2',
      mt: '',
      pedestal: 'h-28 bg-gradient-to-t from-yellow-400/20 to-yellow-50',
    },
    2: {
      size: 'w-16 h-16',
      ring: 'ring-4 ring-gray-300',
      badge: 'bg-gradient-to-br from-gray-300 to-gray-400',
      icon: Medal,
      glow: 'shadow-lg',
      order: 'order-1',
      mt: 'mt-8',
      pedestal: 'h-20 bg-gradient-to-t from-gray-200/30 to-gray-50',
    },
    3: {
      size: 'w-16 h-16',
      ring: 'ring-4 ring-amber-600',
      badge: 'bg-gradient-to-br from-amber-600 to-amber-700',
      icon: Medal,
      glow: 'shadow-lg',
      order: 'order-3',
      mt: 'mt-8',
      pedestal: 'h-16 bg-gradient-to-t from-amber-100/30 to-amber-50/10',
    },
  };

  const config = rankConfig[rank];

  return (
    <div className={`flex flex-col items-center ${config.order} ${config.mt} flex-1 max-w-[200px]`}>
      <ProfileHoverCard user={entry.user}>
        <div className="relative cursor-pointer">
          <div className={`absolute -top-2 -right-2 z-10 ${config.badge} text-white rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold shadow-md`}>
            {rank}
          </div>
          {profileUrl ? (
            <img
              src={profileUrl}
              alt=""
              className={`${config.size} rounded-full object-cover ${config.ring} ${config.glow}`}
            />
          ) : (
            <div className={`${config.size} rounded-full bg-gradient-to-br from-[#1086a4] to-[#054581] flex items-center justify-center text-white font-bold ${config.ring} ${config.glow} ${rank === 1 ? 'text-2xl' : 'text-base'}`}>
              {initials}
            </div>
          )}
          {rank === 1 && (
            <div className="absolute -top-5 left-1/2 -translate-x-1/2">
              <Crown size={22} className="text-yellow-400 fill-yellow-400" />
            </div>
          )}
        </div>
      </ProfileHoverCard>
      <p className={`mt-3 font-semibold text-sm text-gray-800 text-center max-w-[120px] truncate ${isCurrentUser ? 'text-[#054581]' : ''}`}>
        {entry.user?.name} {entry.user?.lastName?.[0]}.
      </p>
      {isCurrentUser && (
        <span className="text-[10px] bg-[#1086a4] text-white px-2 py-0.5 rounded-full mt-0.5">You</span>
      )}
      <div className="flex items-center gap-1 mt-1">
        <span className="font-bold text-[#054581] text-lg">{(entry.value || 0).toLocaleString()}</span>
        <span className="text-[10px] text-gray-400 uppercase tracking-wide">{(METRIC_CONFIG[activeTab] || METRIC_CONFIG.xp).label}</span>
      </div>
      {/* Pedestal bar */}
      <div className={`w-full mt-3 rounded-t-xl ${config.pedestal}`} />
    </div>
  );
};

// ── Stat Card ───────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3 shadow-sm">
    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
      <Icon size={20} style={{ color }} />
    </div>
    <div>
      <p className="text-xs text-gray-400 font-medium">{label}</p>
      <p className="text-lg font-bold text-gray-800">{value}</p>
    </div>
  </div>
);

// ── Main Leaderboard ────────────────────────────────────────────────────────
class Leaderboard extends Component {
  static propTypes = {
    auth: PropTypes.object.isRequired,
    xp: PropTypes.object.isRequired,
    program: PropTypes.object.isRequired,
    getXPLeaderboard: PropTypes.func.isRequired,
    getPrograms: PropTypes.func.isRequired
  };

  state = {
    selectedProgramId: null,
    initialized: false,
    programDropdownOpen: false
  };

  componentDidMount() {
    const { user } = this.props.auth;
    const isAdmin = user && (user.role === 'admin' || user.role === 'platform_admin' || user.role === 'instructor');

    if (isAdmin) {
      this.props.getPrograms();
    }

    const programId = user?.group?.program?._id || user?.group?.program || null;
    const metric = user?.group?.program?.leaderboardMetric || 'xp';
    this.setState({ selectedProgramId: programId, initialized: true });
    this.props.getXPLeaderboard(10, programId, metric);

    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentDidUpdate(prevProps) {
    const prevPrograms = prevProps.program.programs;
    const currPrograms = this.props.program.programs;
    // Re-fetch with correct metric once programs finish loading
    if (prevPrograms.length === 0 && currPrograms.length > 0) {
      const metric = this.getMetricForProgram(this.state.selectedProgramId);
      this.props.getXPLeaderboard(10, this.state.selectedProgramId, metric);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  dropdownRef = React.createRef();

  handleClickOutside = (e) => {
    if (this.dropdownRef.current && !this.dropdownRef.current.contains(e.target)) {
      this.setState({ programDropdownOpen: false });
    }
  };

  getMetricForProgram = (programId) => {
    if (!programId) return 'xp';
    // First try the full programs list (available for admins)
    const { programs } = this.props.program;
    const found = programs.find(p => p._id === programId);
    if (found?.leaderboardMetric) return found.leaderboardMetric;
    // Fallback to the user's own program data (from auth populate)
    const userProgram = this.props.auth.user?.group?.program;
    if (userProgram && (userProgram._id === programId || userProgram === programId)) {
      return userProgram.leaderboardMetric || 'xp';
    }
    return 'xp';
  };

  fetchLeaderboard = (programId) => {
    const metric = this.getMetricForProgram(programId);
    this.props.getXPLeaderboard(10, programId, metric);
  };

  onProgramSelect = (programId) => {
    this.setState({ selectedProgramId: programId, programDropdownOpen: false });
    this.fetchLeaderboard(programId);
  };

  getSelectedProgramTitle = () => {
    const { programs } = this.props.program;
    const { selectedProgramId } = this.state;
    if (!selectedProgramId) return 'All Programs';
    const found = programs.find(p => p._id === selectedProgramId);
    return found ? found.title : 'All Programs';
  };

  render() {
    const { leaderboard, loading } = this.props.xp;
    const { programs } = this.props.program;
    const { user } = this.props.auth;
    const { initialized, programDropdownOpen, selectedProgramId } = this.state;
    const activeTab = this.getMetricForProgram(selectedProgramId);

    const isAdmin = user && (user.role === 'admin' || user.role === 'platform_admin' || user.role === 'instructor');

    if (!initialized) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin h-10 w-10 border-4 border-[#1db5d4] border-t-transparent rounded-full" />
        </div>
      );
    }

    // Limit to 10 entries
    const entries = leaderboard.slice(0, 10);
    const top3 = entries.slice(0, 3);
    const rest = entries.slice(3);
    const ActiveTabConfig = METRIC_CONFIG[activeTab] || METRIC_CONFIG.xp;

    // Stats
    const totalParticipants = entries.length;
    const topScore = entries.length > 0 ? entries[0].value : 0;
    const userId = user?._id || user?.id;
    const yourRank = entries.findIndex(e => String(e.user?._id) === String(userId));
    const yourRankDisplay = yourRank >= 0 ? `#${yourRank + 1}` : 'Unranked';

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
        <div className="max-w-5xl mx-auto py-8 px-4 sm:px-6">

          {/* ── Header ── */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#054581] to-[#1db5d4] rounded-2xl mb-4 shadow-lg shadow-[#1086a4]/20">
              <Trophy size={32} className="text-white" />
            </div>
            <div className="flex items-center justify-center gap-2">
              <h1 className="text-3xl font-bold text-gray-900">Leaderboard</h1>
              <span className="px-2.5 py-0.5 text-xs font-semibold bg-amber-100 text-amber-700 rounded-full uppercase tracking-wide">Beta</span>
            </div>
            <p className="text-gray-400 text-sm mt-1">See who's leading the way — this feature is still in testing</p>
            <div className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 bg-white border border-gray-200 rounded-full shadow-sm">
              <ActiveTabConfig.icon size={14} style={{ color: ActiveTabConfig.color }} />
              <span className="text-xs font-medium text-gray-600">Ranked by</span>
              <span className="text-xs font-semibold" style={{ color: ActiveTabConfig.color }}>{ActiveTabConfig.label}</span>
            </div>
          </div>

          {/* ── Program Selector (admin/instructor) ── */}
          {isAdmin && programs.length > 0 && (
            <div className="flex justify-center mb-6" ref={this.dropdownRef}>
              <div className="relative">
                <button
                  onClick={() => this.setState(s => ({ programDropdownOpen: !s.programDropdownOpen }))}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 hover:border-[#1086a4] transition-colors shadow-sm"
                >
                  <span className="font-medium">{this.getSelectedProgramTitle()}</span>
                  <ChevronDown size={16} className={`text-gray-400 transition-transform ${programDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {programDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-1 z-50 animate-fade-in">
                    <button
                      onClick={() => this.onProgramSelect(null)}
                      className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 text-gray-700 transition-colors"
                    >
                      All Programs
                    </button>
                    {programs.map(p => (
                      <button
                        key={p._id}
                        onClick={() => this.onProgramSelect(p._id)}
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 text-gray-700 transition-colors"
                      >
                        {p.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Stat Cards ── */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <StatCard icon={Users} label="Participants" value={totalParticipants} color="#1086a4" />
            <StatCard icon={Hash} label="Your Rank" value={yourRankDisplay} color="#054581" />
            <StatCard icon={TrendingUp} label="Top Score" value={topScore.toLocaleString()} color="#f59e0b" />
          </div>

          {/* ── Loading ── */}
          {loading && leaderboard.length === 0 && (
            <div className="flex justify-center py-20">
              <div className="animate-spin h-8 w-8 border-3 border-[#1db5d4] border-t-transparent rounded-full" />
            </div>
          )}

          {/* ── Empty State ── */}
          {!loading && leaderboard.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-2xl flex items-center justify-center">
                <Trophy size={28} className="text-gray-300" />
              </div>
              <p className="text-gray-500 font-medium">No rankings yet</p>
              <p className="text-gray-400 text-sm mt-1">Complete activities to appear here!</p>
            </div>
          )}

          {/* ── Content ── */}
          {entries.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

              {/* ── Left: Top 3 Podium ── */}
              <div className="bg-gradient-to-b from-[#f0f9ff] to-white rounded-3xl p-8 border border-blue-100/50 flex flex-col">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-6 text-center">Top 3</h3>
                <div className="flex justify-center items-end gap-4 sm:gap-8 flex-1">
                  {[2, 1, 3].map(rank => {
                    const entry = top3[rank - 1];
                    if (!entry) return null;
                    return (
                      <PodiumCard
                        key={rank}
                        entry={entry}
                        rank={rank}
                        isCurrentUser={String(entry.user?._id) === String(userId)}
                        activeTab={activeTab}
                      />
                    );
                  })}
                </div>
              </div>

              {/* ── Right: Ranks 4–10 ── */}
              <div className="bg-white rounded-3xl border border-gray-100 flex flex-col">
                <div className="px-5 py-4 border-b border-gray-50">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Ranks 4–10</h3>
                </div>
                {rest.length > 0 ? (
                  <div className="divide-y divide-gray-50 flex-1">
                    {rest.map((entry, index) => {
                      const rank = index + 4;
                      const isCurrentUser = String(entry.user?._id) === String(userId);
                      const profileUrl = entry.user?.profilePicture?.url;
                      const initials = ((entry.user?.name?.[0] || '') + (entry.user?.lastName?.[0] || '')).toUpperCase();

                      return (
                        <div
                          key={entry.user?._id || index}
                          className={`flex items-center px-5 py-3.5 hover:bg-gray-50/50 transition-colors ${
                            isCurrentUser ? 'bg-[#f0f9ff]' : ''
                          }`}
                        >
                          {/* Rank */}
                          <span className="w-8 text-center text-sm font-bold text-gray-300 flex-shrink-0">
                            {rank}
                          </span>

                          {/* Avatar */}
                          <ProfileHoverCard user={entry.user} position="below">
                            <div className="flex-shrink-0 mx-3 cursor-pointer">
                              {profileUrl ? (
                                <img
                                  src={profileUrl}
                                  alt=""
                                  className="w-10 h-10 rounded-full object-cover ring-2 ring-gray-100"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1086a4] to-[#054581] flex items-center justify-center text-white text-xs font-bold ring-2 ring-gray-100">
                                  {initials}
                                </div>
                              )}
                            </div>
                          </ProfileHoverCard>

                          {/* Name */}
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${isCurrentUser ? 'text-[#054581]' : 'text-gray-700'}`}>
                              {entry.user?.name} {entry.user?.lastName}
                              {isCurrentUser && (
                                <span className="ml-2 text-[10px] bg-[#1086a4]/10 text-[#1086a4] px-1.5 py-0.5 rounded-full font-semibold">You</span>
                              )}
                            </p>
                          </div>

                          {/* Value */}
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {ActiveTabConfig && <ActiveTabConfig.icon size={14} style={{ color: ActiveTabConfig.color }} />}
                            <span className="font-bold text-gray-800 text-sm">{(entry.value || 0).toLocaleString()}</span>
                            <span className="text-[10px] text-gray-400 uppercase">{ActiveTabConfig.label}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-gray-400 text-sm py-8">
                    Not enough participants yet
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  auth: state.auth,
  xp: state.xp,
  program: state.program
});

export default connect(mapStateToProps, { getXPLeaderboard, getPrograms })(Leaderboard);
